import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import { categoryOptions } from '@features/admin/reports/reportData';
import {
  TReportActiveUsersByProductCategoriesData,
  ReportCategories,
  TReportActiveUsersByProductCategoriesPayload,
} from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  exportActiveUsersByProductCategories,
  getActiveUsersByProductCategories,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { UserRolePermission } from '@src/types/settings';
import { canAccess } from '@src/utils/auth';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportActiveUsersByProductCategoriesData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.category}
        </p>
        <p className="text-white text-xs">Active Users: {payload[0]?.value}</p>
      </div>
    );
  }

  return null;
};

export const ActiveUsersByProductCategories: FC = () => {
  const { auth } = useAuthStore();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [data, setData] = useState<TReportActiveUsersByProductCategoriesData[]>(
    []
  );
  const [summaryData, setSummaryData] = useState<
    | {
        active_users: number;
      }
    | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportActiveUsersByProductCategoriesData>({
      data,
      getAccessField: (item) => item.category,
      getMainDataField: (item) => item.active_users || 0,
      columnTitleFormatter: () => 'ACTIVE USERS',
      includeTotalsRow: true,
      summaryRow: summaryData,
      customColumns: [
        {
          title: 'ACTIVE USERS',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">{item?.active_users ?? 0}</p>
          ),
        },
      ],
    });

  const handleCategoriesChange = (values: string[]) => {
    setSelectedCategories(values);
  };

  const buildPayload = () => {
    const isAddonIncluded = selectedCategories.includes(
      ReportCategories.ADD_ONS
    );
    const isMonthlySubIncluded = selectedCategories.includes(
      ReportCategories.ALL_MONTHLY_EVALUATIONS
    );

    const payload: TReportActiveUsersByProductCategoriesPayload = {
      account_type: selectedCategories.filter(
        (category) =>
          category !== ReportCategories.ADD_ONS &&
          category !== ReportCategories.ALL_MONTHLY_EVALUATIONS
      ),
      add_ons: selectedCategories.length > 0 ? isAddonIncluded : true,
      monthly_subscription:
        selectedCategories.length > 0 ? isMonthlySubIncluded : true,
    };

    return payload;
  };

  const handleRunReport = async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getActiveUsersByProductCategories(payload);

      setData(response.data);
      setSummaryData(response.summary);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildPayload();
      const response = await exportActiveUsersByProductCategories(payload);

      downloadFile(response.data, 'active-users-by-product-categories');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Active Users by Product Categories
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCategoriesFilter
          categoryOptions={categoryOptions}
          isLoading={isLoading}
          selectedCategories={selectedCategories}
          showGroupByFilter={false}
          showPeriodFilter={false}
          onCategoriesChange={handleCategoriesChange}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="category"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                barSize={60}
                dataKey="active_users"
                fill="#B6D72C"
                name="Active Users"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
